import { Component, Inject, OnInit } from '@angular/core';

import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NgxCurrencyDirective } from 'ngx-currency';
import { SelectPesquisavelComponent } from '../../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { EstoqueLoteSerieInlineComponent } from '../../../../../../shared/components/estoque-lote-serie-inline/estoque-lote-serie-inline.component';
import { AlertCardComponent } from '../../../../../../shared/components/alert-card/alert-card.component';
import { EmptyStateComponent } from '../../../../../../shared/components/empty-state/empty-state.component';
import { LookupItemStruct } from '../../../../../../shared/services/structs/pessoa/lookup-item.struct';

@Component({
  selector: 'app-variacao-estoque-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    NgxCurrencyDirective,
    SelectPesquisavelComponent,
    EstoqueLoteSerieInlineComponent,
    AlertCardComponent,
    EmptyStateComponent,
  ],
  templateUrl: './variacao-estoque-modal.component.html',
  styleUrl: './variacao-estoque-modal.component.css',
})
export class VariacaoEstoqueModalComponent implements OnInit {
  public form: FormGroup;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<VariacaoEstoqueModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      nomeVariavel: string;
      estoqueAtual: any[];
      listaLocalizacaoEstoque: any[];
      tipoRastreio?: 'lote' | 'serie' | null;
      isEditMode?: boolean;
      quantidadeMask?: any;
      isFracionada?: boolean;
    }
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    if (this.data.estoqueAtual && this.data.estoqueAtual.length > 0) {
      this.data.estoqueAtual.forEach(e => this.addEstoqueRow(e));
    }

    // Check for default stock location
    if (this.data.listaLocalizacaoEstoque && this.data.listaLocalizacaoEstoque.length > 0) {
      const defaultLocation = this.data.listaLocalizacaoEstoque.find(l => l.isPadrao);
      if (defaultLocation) {
        const exists = this.estoqueArray.controls.some(
          ctrl => ctrl.get('idLocalizacaoEstoque')?.value === defaultLocation.idLocalizacaoEstoque
        );

        if (!exists) {
          this.addEstoqueRow({
            idLocalizacaoEstoque: defaultLocation.idLocalizacaoEstoque,
            saldoFisico: 0,
            saldoDisponivel: 0,
            estoqueMinimo: 0,
            estoqueMaximo: 0,
          });
        }
      }
    }

    if (this.estoqueArray.length === 0) {
      this.addEstoqueRow();
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      estoque: this.fb.array([]),
    });
  }

  get estoqueArray(): FormArray {
    return this.form.get('estoque') as FormArray;
  }

  addEstoqueRow(data?: any): void {
    const group = this.fb.group({
      idLocalizacaoEstoque: [
        { value: data?.idLocalizacaoEstoque || null, disabled: this.isLocalPadrao(data?.idLocalizacaoEstoque) },
        Validators.required,
      ],
      saldoFisico: [data?.saldoFisico ?? null],
      saldoDisponivel: [data?.saldoDisponivel ?? null],
      estoqueMinimo: [data?.estoqueMinimo ?? null],
      estoqueMaximo: [data?.estoqueMaximo ?? null],
      idEstoqueSaldoProduto: [data?.idEstoqueSaldoProduto || null],
      lotes: [data?.lotes || []],
      series: [data?.series || []],
      loteSerieCompleto: [true],
    });
    this.estoqueArray.push(group);
  }

  removeEstoqueRow(index: number): void {
    this.estoqueArray.removeAt(index);
  }

  cancelar(): void {
    this.dialogRef.close();
  }

  confirmar(): void {
    if (this.form.invalid || this.temPendencia) {
      this.form.markAllAsTouched();
      return;
    }
    this.dialogRef.close(this.estoqueArray.getRawValue());
  }

  public isLocalPadrao(id: number): boolean {
    const item = this.data.listaLocalizacaoEstoque?.find(l => l.idLocalizacaoEstoque === id);
    return item?.isPadrao || false;
  }

  /** Locais disponíveis para a linha `item`: exclui os já usados em outras linhas para não permitir duplicidade. */
  public localizacoesDisponiveis(item: AbstractControl): any[] {
    const idAtual = item.get('idLocalizacaoEstoque')?.value;
    const idsUsadosEmOutrasLinhas = this.estoqueArray.controls
      .filter(c => c !== item)
      .map(c => c.get('idLocalizacaoEstoque')?.value)
      .filter(id => id != null);
    return (this.data.listaLocalizacaoEstoque ?? []).filter(
      l => l.idLocalizacaoEstoque === idAtual || !idsUsadosEmOutrasLinhas.includes(l.idLocalizacaoEstoque)
    );
  }

  // ── Balanço inicial: lote/série por local ──────────────────────────────────

  public get tipoRastreio(): 'lote' | 'serie' | null {
    return this.data.tipoRastreio ?? null;
  }

  public mostrarPainelRastreio(item: AbstractControl): boolean {
    return !this.data.isEditMode && this.tipoRastreio !== null && (Number(item.get('saldoFisico')?.value) || 0) > 0;
  }

  public itensRastreio(item: AbstractControl): any[] {
    const campo = this.tipoRastreio === 'serie' ? 'series' : 'lotes';
    return item.get(campo)?.value ?? [];
  }

  /** Códigos de série já usados em OUTROS locais desta variação — a série identifica uma
   * unidade física única, então não pode se repetir entre locais de estoque diferentes. */
  public codigosSerieEmOutrosLocais(itemAtual: AbstractControl): string[] {
    return this.estoqueArray.controls
      .filter(c => c !== itemAtual)
      .flatMap(c => (c.get('series')?.value ?? []) as any[])
      .map(s => s.codigoSerie)
      .filter((c): c is string => !!c);
  }

  public nomeLocal(item: AbstractControl): string {
    const id = item.get('idLocalizacaoEstoque')?.value;
    return this.data.listaLocalizacaoEstoque?.find(l => l.idLocalizacaoEstoque === id)?.nome ?? '';
  }

  public onItensRastreioChange(item: AbstractControl, itens: any[]): void {
    const campo = this.tipoRastreio === 'serie' ? 'series' : 'lotes';
    item.get(campo)?.setValue(itens);
  }

  public onCompletoChange(item: AbstractControl, completo: boolean): void {
    item.get('loteSerieCompleto')?.setValue(completo);
  }

  public linhaPendente(item: AbstractControl): boolean {
    return this.mostrarPainelRastreio(item) && item.get('loteSerieCompleto')?.value !== true;
  }

  public get temPendencia(): boolean {
    return this.estoqueArray.controls.some(c => this.linhaPendente(c));
  }
}
